import React from 'react';
import { 
  MoreHorizontal, 
  Building2, 
  Calendar 
} from 'lucide-react';

// Card Header Component
const CardHeader = ({ title, subtitle, moreButton = true }) => (
  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
    <div>
      <h2 className="font-semibold text-gray-800">{title}</h2>
      {subtitle && <span className="text-sm text-gray-500">{subtitle}</span>}
    </div>
    {moreButton && (
      <button className="text-gray-400 hover:text-gray-600">
        <MoreHorizontal className="w-5 h-5" />
      </button>
    )}
  </div>
);

// Appointment Component
const AppointmentItem = ({ type, title, address, time }) => (
  <div className="flex items-start gap-4 px-6 py-4 hover:bg-gray-50">
    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
      {type === 'LISTING' ? (
        <Building2 className="w-5 h-5 text-blue-500" />
      ) : (
        <Calendar className="w-5 h-5 text-blue-500" />
      )}
    </div>
    <div className="flex-1">
      <h3 className="font-medium text-gray-800">{title}</h3>
      <p className="text-sm text-gray-500">
        <span className="text-xs font-medium text-gray-400">{type}</span> | {address}
      </p>
    </div>
    <div className="text-sm text-gray-500 whitespace-nowrap">{time}</div>
  </div>
);

// Todo Item Component
const TodoItem = ({ priority, type, task, details }) => (
  <div className="flex items-start gap-4 px-6 py-4 hover:bg-gray-50">
    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
      priority === 1 ? 'bg-red-100 text-red-500' :
      priority === 2 ? 'bg-orange-100 text-orange-500' :
      priority === 3 ? 'bg-green-100 text-green-500' :
      'bg-blue-100 text-blue-500'
    }`}>
      {priority}
    </div>
    <div className="flex-1 min-w-0">
      <h3 className="font-medium text-gray-800 mb-1">{task}</h3>
      <p className="text-sm text-gray-500 truncate">
        <span className="text-xs font-medium text-gray-400">{type}</span> | {details}
      </p>
    </div>
  </div>
);

// Progress Section Component
const ProgressSection = ({ title, percentage, tasks, cta }) => (
  <div className="bg-white rounded-xl shadow-sm">
    <div className="p-6">
      <h3 className="font-medium text-gray-800 mb-4">{title}</h3>
      <div className="h-2 bg-gray-100 rounded-full mb-2">
        <div 
          className={`h-full rounded-full ${
            cta === 'Next Listing' ? 'bg-blue-500' :
            cta === 'Next Escrow' ? 'bg-green-500' :
            cta === 'View All' ? 'bg-orange-500' :
            'bg-red-500'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="text-sm text-gray-500 mb-4">{percentage}% Complete</div>
      
      <div className="space-y-3 mb-6">
        {tasks.map((task, index) => (
          <div key={index} className="flex items-center gap-3">
            <input 
              type="checkbox" 
              className="w-4 h-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600">{task}</span>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <button className={`flex-1 px-4 py-2 text-sm text-white rounded-lg ${
          cta === 'Next Listing' ? 'bg-blue-500 hover:bg-blue-600' :
          cta === 'Next Escrow' ? 'bg-green-500 hover:bg-green-600' :
          'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }`}>
          {cta}
        </button>
        {cta !== 'View All' && (
          <button className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">
            View All
          </button>
        )}
      </div>
    </div>
  </div>
);

const DashboardContent = () => {
    return (
      <div className="p-6 bg-gray-195">
        {/* Top Row - Appointments and Todo */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Appointments Card */}
          <div className="bg-white rounded-xl shadow-sm">
            <CardHeader 
              title="Appointments" 
              subtitle="Today, December 14"
            />
            <div className="divide-y divide-gray-100">
              <AppointmentItem 
                type="LISTING"
                title="Showing with Dan"
                address="1234 Main Street"
                time="10AM - 11AM CST"
              />
              <AppointmentItem 
                type="ESCROW"
                title="Meeting with Lender"
                address="https://www.zoom.com/491@5okgmskej9kgskMEET"
                time="2:30PM - 3:30PM CST"
              />
              <AppointmentItem 
                type="LEAD"
                title="Meeting with Sam Smith"
                address="4569 East Street"
                time="4:00PM - 5:00PM CST"
              />
              <AppointmentItem 
                type="OTHER"
                title="Benjamin Smith's Birthday"
                address=""
                time="ALL DAY"
              />
            </div>
          </div>
  
          {/* Todo Card */}
          <div className="bg-white rounded-xl shadow-sm">
            <CardHeader title="To Do" />
            <div className="divide-y divide-gray-100">
              <TodoItem 
                priority={1}
                type="LISTING"
                task="Contact Staging Company for Quote on Entire Home"
                details="1234 Main Street | Need furnishing for bedrooms, 1 office, kitchen..."
              />
              <TodoItem 
                priority={2}
                type="LEAD"
                task="Collect Budget and Preferences from Shannon"
                details="Shannon Parker has reached out but with the need for a home in South For..."
              />
              <TodoItem 
                priority={3}
                type="LEAD"
                task="Research Market Trends in the Dallas/Fort Worth area"
                details="Notes for this task would show up here"
              />
              <TodoItem 
                priority={1}
                type="ESCROW"
                task="Coordinate Offer between Mark Smith and Core Realty"
                details="49802 South Park Street | Notes for this task would show up here"
              />
            </div>
          </div>
        </div>
  
        {/* Middle Row - Listings, Escrows, Leads */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Listings */}
          <div className="bg-white rounded-xl shadow-sm">
            <CardHeader title="Listings" />
            <div className="px-6 py-4">
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-gray-500 border-b border-gray-100">
                    <th className="pb-2 font-medium text-left">ADDRESS</th>
                    <th className="pb-2 font-medium text-right">PRICE</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="py-3 text-blue-500">1234 Main Street</td>
                    <td className="py-3 text-right">$565,000</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-blue-500">4295 Eastern Bay Street</td>
                    <td className="py-3 text-right">$1,100,000</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-blue-500">391 San Marco Lane</td>
                    <td className="py-3 text-right">$465,000</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-blue-500">4692 Timberland Cove Unit 5</td>
                    <td className="py-3 text-right">$465,000</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
  
          {/* Escrows */}
          <div className="bg-white rounded-xl shadow-sm">
            <CardHeader title="Escrows" />
            <div className="px-6 py-4">
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-gray-500 border-b border-gray-100">
                    <th className="pb-2 font-medium text-left">ADDRESS</th>
                    <th className="pb-2 font-medium text-right">CLOSING DATE</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="py-3 text-green-500">5413 Bankside Terrace</td>
                    <td className="py-3 text-right">March 3</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-green-500">4633 State Road #594</td>
                    <td className="py-3 text-right">Feb 12</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-green-500">5920 High Ridge Road</td>
                    <td className="py-3 text-right">Feb 24</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-green-500">6023 Wrightsville Street</td>
                    <td className="py-3 text-right">March 1</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
  
          {/* Leads */}
          <div className="bg-white rounded-xl shadow-sm">
            <CardHeader title="Leads" />
            <div className="px-6 py-4">
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-gray-500 border-b border-gray-100">
                    <th className="pb-2 font-medium text-left">NAME</th>
                    <th className="pb-2 font-medium text-right">QUALITY SCORE</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="py-3 text-orange-500">Shannon Steveton</td>
                    <td className="py-3 text-right">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-500">1</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 text-orange-500">Marcus Donelson</td>
                    <td className="py-3 text-right">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-500">1</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 text-orange-500">Joshua Schweistol</td>
                    <td className="py-3 text-right">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-orange-100 text-orange-500">2</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 text-orange-500">Samantha Collin</td>
                    <td className="py-3 text-right">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-red-500">4</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
  
        {/* Bottom Row - Suggestions */}
        <div className="mt-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Suggestions for You</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <ProgressSection 
              title="Listing: 4529 Eastern Bay Street"
              percentage={24}
              tasks={[
                "Post 3D Tour to Realtor.com",
                "Schedule an Open House Event",
                "Set Up Showing in CSS",
                "Put Up Drive By Text Tech Rider"
              ]}
              cta="Next Listing"
            />
            <ProgressSection 
              title="Escrow: 2947 Southwest Burns Road"
              percentage={82}
              tasks={[
                "Send Survey to Buyer's Agent",
                "Negotiate Repairs with Seller",
                "Send Amendments to Title C...",
                "Contact Buyers to Confirm D..."
              ]}
              cta="Next Escrow"
            />
            <ProgressSection 
              title="Prospecting"
              percentage={50}
              tasks={[
                "Reach out to Lenders at the...",
                "Contact Samantha Anderson",
                "Generate an Ad for $948 Brie...",
                "Put Up Drive By Text Tech Rider"
              ]}
              cta="View All"
            />
            <ProgressSection 
              title="Social Media"
              percentage={30}
              tasks={[
                "Post Daily Social Content",
                "Create an Account for...",
                "Generate an Facebook List...",
                "Refresh Templates on Canva"
              ]}
              cta="View All"
            />
          </div>
        </div>
      </div>
    );
  };
  
  export default DashboardContent;
  